/**
 * Studio: Market Structure (SMS)
 *
 * Price-action market-structure indicator modeled after TradingView's
 * open-source "Studio: Market Structure (SMS)" script by studioAlgo.
 *
 * What it draws (mirrors the TradingView script's plot inventory):
 * - Confirmed swing highs (SH) and swing lows (SL) pivots
 * - Structure mapping: HH / HL / LH / LL labels on every confirmed swing
 * - SH1-3 / SL1-3 structural levels extending forward to the right edge
 * - Break of Structure detection with "Bullish/Bearish Structure Break"
 *   labels plus Buy/Sell break signals
 * - Volume-weighted EMA "Magic Line" for trend bias / trailing stop
 * - Fair Value Gap (FVG) zones drawn as forward-extended top/bottom lines
 * - Optional ADX threshold filter to suppress signals in sideways markets
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";

interface Swing {
  index: number;
  price: number;
}

interface FvgZone {
  index: number;
  top: number;
  bottom: number;
  bull: boolean;
}

/** Wilder ADX (14-period style) aligned to candle indexes; NaN where undefined. */
const computeAdx = (candles: CandleData[], period: number): number[] => {
  const n = candles.length;
  const adx: number[] = new Array(n).fill(NaN);
  if (n < period * 2 + 2) return adx;

  const tr: number[] = [];
  const plusDM: number[] = [];
  const minusDM: number[] = [];
  for (let i = 1; i < n; i++) {
    const up = candles[i].high - candles[i - 1].high;
    const down = candles[i - 1].low - candles[i].low;
    plusDM.push(up > down && up > 0 ? up : 0);
    minusDM.push(down > up && down > 0 ? down : 0);
    tr.push(
      Math.max(
        candles[i].high - candles[i].low,
        Math.abs(candles[i].high - candles[i - 1].close),
        Math.abs(candles[i].low - candles[i - 1].close)
      )
    );
  }

  let atr = 0;
  let pdm = 0;
  let mdm = 0;
  for (let i = 0; i < period; i++) {
    atr += tr[i];
    pdm += plusDM[i];
    mdm += minusDM[i];
  }

  const dx: { index: number; value: number }[] = [];
  const pushDx = (candleIndex: number) => {
    const diPlus = atr > 0 ? (100 * pdm) / atr : 0;
    const diMinus = atr > 0 ? (100 * mdm) / atr : 0;
    const sum = diPlus + diMinus;
    dx.push({ index: candleIndex, value: sum > 0 ? (100 * Math.abs(diPlus - diMinus)) / sum : 0 });
  };
  pushDx(period);

  for (let i = period; i < tr.length; i++) {
    atr = atr - atr / period + tr[i];
    pdm = pdm - pdm / period + plusDM[i];
    mdm = mdm - mdm / period + minusDM[i];
    pushDx(i + 1);
  }

  if (dx.length < period) return adx;
  let running = 0;
  for (let i = 0; i < period; i++) running += dx[i].value;
  adx[dx[period - 1].index] = running / period;
  for (let i = period; i < dx.length; i++) {
    const prev = adx[dx[i - 1].index];
    adx[dx[i].index] = (prev * (period - 1) + dx[i].value) / period;
  }
  return adx;
};

export const studioMarketStructureIndicator: IndicatorDefinition = {
  id: "studio_market_structure",
  name: "Studio: Market Structure (SMS)",
  shortName: "SMS",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "swingLength", label: "Swing Length", type: "number", default: 5, min: 1, max: 50 },
    { key: "levelsCount", label: "SH/SL Levels", type: "number", default: 3, min: 1, max: 3 },
    { key: "showStructureLabels", label: "HH/HL/LH/LL Labels", type: "boolean", default: true },
    { key: "showBreakSignals", label: "Buy/Sell Signals", type: "boolean", default: true },
    { key: "showBreakLabels", label: "Structure Break Labels", type: "boolean", default: true },
    { key: "showMagicLine", label: "Magic Line", type: "boolean", default: true },
    { key: "magicLength", label: "Magic Line Length", type: "number", default: 20, min: 2, max: 200 },
    { key: "showFvg", label: "Fair Value Gaps", type: "boolean", default: true },
    { key: "fvgMinPct", label: "FVG Min %", type: "number", default: 0.05, min: 0, max: 5, step: 0.05 },
    { key: "adxThreshold", label: "ADX Filter (0 = off)", type: "number", default: 0, min: 0, max: 50 },
    { key: "bullColor", label: "Bull Color", type: "color", default: "#089981" },
    { key: "bearColor", label: "Bear Color", type: "color", default: "#F23645" },
    { key: "levelColor", label: "Level Color", type: "color", default: "#787B86" },
  ],
  plots: [
    {
      key: "magicLine",
      label: "Magic Line",
      color: "#2962FF",
      style: "line",
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
    },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const {
      swingLength,
      levelsCount,
      showStructureLabels,
      showBreakSignals,
      showBreakLabels,
      showMagicLine,
      magicLength,
      showFvg,
      fvgMinPct,
      adxThreshold,
      bullColor,
      bearColor,
      levelColor,
    } = params;

    const markers: IndicatorMarker[] = [];
    const priceLines: IndicatorPriceLine[] = [];
    const n = candles.length;
    const len = Math.max(1, Math.round(swingLength));

    const emptyResult: PlotData[] = [{ key: "magicLine", data: [], markers, priceLines }];
    if (n < 3) return emptyResult;

    // ── 1. Detect confirmed swing highs / swing lows ───────────────
    // A pivot at index i is confirmed only after `len` bars to its right.
    const swingHighs: Swing[] = [];
    const swingLows: Swing[] = [];
    for (let i = len; i <= n - 1 - len; i++) {
      let isHigh = true;
      let isLow = true;
      for (let j = 1; j <= len && (isHigh || isLow); j++) {
        if (isHigh && (candles[i].high <= candles[i - j].high || candles[i].high <= candles[i + j].high)) {
          isHigh = false;
        }
        if (isLow && (candles[i].low >= candles[i - j].low || candles[i].low >= candles[i + j].low)) {
          isLow = false;
        }
      }
      if (isHigh) swingHighs.push({ index: i, price: candles[i].high });
      if (isLow) swingLows.push({ index: i, price: candles[i].low });
    }

    // ── 2. Optional ADX filter ─────────────────────────────────────
    const adxOn = Number(adxThreshold) > 0;
    const adx = adxOn ? computeAdx(candles, 14) : null;

    // ── 3. Walk bars: track confirmed levels, detect breaks ────────
    let highPtr = 0;
    let lowPtr = 0;
    let lastHigh: Swing | null = null;
    let lastLow: Swing | null = null;
    let lastBreakSide: "bull" | "bear" | null = null;

    for (let i = 1; i < n; i++) {
      // Swings confirmed at (or before) this bar become active levels.
      while (highPtr < swingHighs.length && swingHighs[highPtr].index + len <= i) {
        lastHigh = swingHighs[highPtr];
        if (lastBreakSide === "bull") lastBreakSide = null; // level changed, allow a fresh signal
        highPtr++;
      }
      while (lowPtr < swingLows.length && swingLows[lowPtr].index + len <= i) {
        lastLow = swingLows[lowPtr];
        if (lastBreakSide === "bear") lastBreakSide = null;
        lowPtr++;
      }

      const prevClose = candles[i - 1].close;
      const close = candles[i].close;
      const adxFast = adx && !isNaN(adx[i]) ? adx[i] >= Number(adxThreshold) : !adxOn;

      let bullBreak = false;
      let bearBreak = false;
      if (lastHigh && lastLow && adxFast) {
        if (lastBreakSide !== "bull" && prevClose <= lastHigh.price && close > lastHigh.price) {
          bullBreak = true;
          lastBreakSide = "bull";
        } else if (lastBreakSide !== "bear" && prevClose >= lastLow.price && close < lastLow.price) {
          bearBreak = true;
          lastBreakSide = "bear";
        }
      }

      if (bullBreak) {
        if (showBreakSignals) {
          markers.push({
            time: candles[i].time,
            position: "belowBar",
            color: bullColor,
            shape: "arrowUp",
            text: "Buy",
            size: 2,
          });
        }
        if (showBreakLabels) {
          markers.push({
            time: candles[i].time,
            position: "aboveBar",
            color: bullColor,
            shape: "square",
            text: "Bullish Structure Break",
            size: 1,
          });
        }
      } else if (bearBreak) {
        if (showBreakSignals) {
          markers.push({
            time: candles[i].time,
            position: "aboveBar",
            color: bearColor,
            shape: "arrowDown",
            text: "Sell",
            size: 2,
          });
        }
        if (showBreakLabels) {
          markers.push({
            time: candles[i].time,
            position: "belowBar",
            color: bearColor,
            shape: "square",
            text: "Bearish Structure Break",
            size: 1,
          });
        }
      }
    }

    // ── 4. Structure mapping: HH / HL / LH / LL labels on pivots ───
    const labelSwings = (swings: Swing[], isHigh: boolean) => {
      for (let k = 0; k < swings.length; k++) {
        const s = swings[k];
        let label = "";
        let color = levelColor;
        if (k > 0) {
          const bull = isHigh ? s.price > swings[k - 1].price : s.price > swings[k - 1].price;
          label = isHigh ? (bull ? "HH" : "LH") : bull ? "HL" : "LL";
          color = bull ? bullColor : bearColor;
        }
        markers.push({
          time: candles[s.index].time,
          position: isHigh ? "aboveBar" : "belowBar",
          color,
          shape: "circle",
          text: showStructureLabels ? label : "",
          size: 1,
        });
      }
    };
    labelSwings(swingHighs, true);
    labelSwings(swingLows, false);

    // ── 5. SH1-3 / SL1-3 structural levels (extend to right edge) ──
    const levels = Math.max(1, Math.min(3, Math.round(levelsCount)));
    for (let k = 0; k < levels && k < swingHighs.length; k++) {
      const s = swingHighs[swingHighs.length - 1 - k];
      priceLines.push({
        id: `sms_sh_${k + 1}`,
        price: s.price,
        color: levelColor,
        title: `SH${k + 1}`,
        lineStyle: 2,
        lineWidth: 1,
        fromTime: candles[s.index].time,
      });
    }
    for (let k = 0; k < levels && k < swingLows.length; k++) {
      const s = swingLows[swingLows.length - 1 - k];
      priceLines.push({
        id: `sms_sl_${k + 1}`,
        price: s.price,
        color: levelColor,
        title: `SL${k + 1}`,
        lineStyle: 2,
        lineWidth: 1,
        fromTime: candles[s.index].time,
      });
    }

    // ── 6. Fair Value Gaps: unfilled zones as forward lines ────────
    if (showFvg) {
      const minPct = Number(fvgMinPct) || 0;
      const active: FvgZone[] = [];
      for (let i = 2; i < n; i++) {
        // Mitigate first: price trading back through an old gap fills it.
        for (let a = active.length - 1; a >= 0; a--) {
          const z = active[a];
          if (z.bull ? candles[i].low <= z.bottom : candles[i].high >= z.top) {
            active.splice(a, 1);
          }
        }
        const c1 = candles[i - 2];
        const c3 = candles[i];
        if (c3.low > c1.high && ((c3.low - c1.high) / c1.high) * 100 >= minPct) {
          active.push({ index: i, top: c3.low, bottom: c1.high, bull: true });
        } else if (c1.low > c3.high && ((c1.low - c3.high) / c3.high) * 100 >= minPct) {
          active.push({ index: i, top: c1.low, bottom: c3.high, bull: false });
        }
      }
      const shown = active.slice(-3);
      shown.forEach((z, k) => {
        const color = z.bull ? "#4CAF50" : "#F23645";
        const suffix = z.bull ? "b" : "r";
        priceLines.push({
          id: `sms_fvg_${suffix}_t${k}`,
          price: z.top,
          color,
          title: "FVG",
          lineStyle: 2,
          lineWidth: 1,
          fromTime: candles[z.index].time,
        });
        priceLines.push({
          id: `sms_fvg_${suffix}_b${k}`,
          price: z.bottom,
          color,
          title: "FVG",
          lineStyle: 2,
          lineWidth: 1,
          fromTime: candles[z.index].time,
        });
      });
    }

    // ── 7. Magic Line: volume-weighted EMA ─────────────────────────
    const magicData: { time: number; value: number }[] = [];
    if (showMagicLine) {
      const period = Math.max(2, Math.round(magicLength));
      if (n >= period) {
        const volumes = candles.map(c => c.volume || 0);
        const avgVol = computeSMA(volumes, period);
        const alpha = 2 / (period + 1);

        let seed = 0;
        for (let i = 0; i < period; i++) seed += candles[i].close;
        let value = seed / period;
        magicData.push({ time: candles[period - 1].time, value });

        for (let i = period; i < n; i++) {
          const avg = avgVol[i];
          const w = avg > 0 ? Math.min(4, Math.max(0.25, volumes[i] / avg)) : 1;
          const eff = (alpha * w) / (1 - alpha + alpha * w);
          value = value * (1 - eff) + candles[i].close * eff;
          magicData.push({ time: candles[i].time, value });
        }
      }
    }

    return [{ key: "magicLine", data: magicData, markers, priceLines }];
  },
};
